<template>
  <div class="review-search-select" :class="{ 'is-open': open }">
    <label v-if="label" class="review-search-select__label" :for="inputId">{{ label }}</label>
    <div class="review-search-select__control">
      <input
        :id="inputId"
        ref="input"
        type="text"
        class="review-search-select__input"
        :placeholder="placeholder"
        :value="inputValue"
        :aria-expanded="open ? 'true' : 'false'"
        aria-autocomplete="list"
        :aria-controls="listId"
        role="combobox"
        autocomplete="off"
        @focus="onFocus"
        @input="onInput"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="pickHighlighted"
        @keydown.esc.prevent="close"
      >
      <button
        v-if="valueId"
        type="button"
        class="review-search-select__clear"
        aria-label="Очистить"
        @click="clear"
      >×</button>
    </div>
    <ul
      v-if="open && filteredOptions.length"
      :id="listId"
      class="review-search-select__list"
      role="listbox"
    >
      <li
        v-for="(opt, idx) in filteredOptions"
        :key="opt.id"
        class="review-search-select__option"
        :class="{ 'is-active': idx === highlight }"
        role="option"
        :aria-selected="opt.id === valueId ? 'true' : 'false'"
        @mousedown.prevent="select(opt)"
      >
        {{ opt.name }}
      </li>
    </ul>
    <p v-else-if="open && query && !filteredOptions.length" class="review-search-select__empty">
      Ничего не найдено
    </p>
  </div>
</template>

<script>
let uid = 0

export default {
  name: 'ReviewSearchSelect',
  props: {
    label: { type: String, default: '' },
    placeholder: { type: String, default: 'Начните вводить…' },
    options: { type: Array, default: () => [] },
    valueId: { type: String, default: '' },
    valueName: { type: String, default: '' },
  },
  data() {
    uid += 1
    return {
      open: false,
      query: '',
      highlight: 0,
      inputId: `review-ss-${uid}`,
      listId: `review-ss-list-${uid}`,
    }
  },
  computed: {
    selectedName() {
      if (this.valueName) return this.valueName
      const found = this.options.find((o) => o.id === this.valueId)
      return found ? found.name : ''
    },
    inputValue() {
      return this.open ? this.query : this.selectedName
    },
    filteredOptions() {
      const q = (this.query || '').trim().toLowerCase()
      if (!q) return this.options.slice(0, 80)
      return this.options
        .filter((o) => String(o.name || '').toLowerCase().includes(q))
        .slice(0, 80)
    },
  },
  watch: {
    valueId() {
      if (!this.open) this.query = ''
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocClick)
  },
  methods: {
    onDocClick(e) {
      if (!this.$el.contains(e.target)) this.close()
    },
    onFocus() {
      this.open = true
      this.query = this.selectedName || ''
      this.highlight = 0
    },
    onInput(e) {
      this.query = e.target.value
      this.open = true
      this.highlight = 0
      if (this.valueId) {
        this.$emit('input', { id: '', name: '' })
      }
    },
    select(opt) {
      this.$emit('input', { id: opt.id, name: opt.name })
      this.query = ''
      this.open = false
    },
    clear() {
      this.$emit('input', { id: '', name: '' })
      this.query = ''
      this.open = false
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus())
    },
    close() {
      this.open = false
      this.query = ''
    },
    move(delta) {
      if (!this.filteredOptions.length) return
      this.open = true
      const len = this.filteredOptions.length
      this.highlight = (this.highlight + delta + len) % len
    },
    pickHighlighted() {
      const opt = this.filteredOptions[this.highlight]
      if (opt) this.select(opt)
    },
  },
}
</script>

<style scoped lang="scss">
.review-search-select {
  position: relative;
  width: 100%;
}

.review-search-select__label {
  display: block;
  margin-bottom: 6px;
  font-family: "Circe", sans-serif;
  font-size: 14px;
  color: #2d3436;
}

.review-search-select__control {
  position: relative;
}

.review-search-select__input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 36px 10px 12px;
  border: 1px solid #d5ddd5;
  border-radius: 8px;
  font-family: "Circe", sans-serif;
  font-size: 15px;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #66cc66;
  }
}

.review-search-select__clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  font-size: 20px;
  line-height: 1;
  color: #6b7280;
  cursor: pointer;
}

.review-search-select__list {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  margin: 0;
  padding: 6px 0;
  list-style: none;
  max-height: 240px;
  overflow: auto;
  background: #fff;
  border: 1px solid #e2e8e2;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.review-search-select__option {
  padding: 8px 12px;
  font-family: "Circe", sans-serif;
  font-size: 14px;
  cursor: pointer;

  &:hover,
  &.is-active {
    background: #f3faf3;
    color: #336633;
  }
}

.review-search-select__empty {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  margin: 0;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e2e8e2;
  border-radius: 8px;
  font-family: "Circe", sans-serif;
  font-size: 13px;
  color: #6b7280;
}
</style>
